// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import * as serviceSelectors from "@modules/service/selectors";
import actionCreator from "@modules/service/actionCreator";
import { Button } from "@components";
import type { StepProps } from "@components";
import { StaffGroupsForm } from "@modules/shared/components";
import type { AlertStaffGroup } from "@modules/service/reducer/alerts/types";

import type { Alert } from "@modules/service/reducer/alerts/types";
type StepData = $Exact<{
  staffGroups: Array<AlertStaffGroup>,
}>;

type OwnProps = $Exact<{
  editing: boolean,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  draft: ?Alert,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreator.SetAlertDraft,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...StepProps,
  ...PropsFromLocalize,
}>;

export class StepRestrictStaffGroups extends React.Component<Props> {
  validate: *;

  componentDidMount() {
    this.validate = new Ajv().compile(this.schema);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.schema && prevProps.schema !== this.schema) {
      this.validate = new Ajv().compile(this.schema);
    }
  }

  get staffGroupsList() {
    const staffGroups = this.schema.staffGroups.items.properties.id.oneOf || [];
    return staffGroups.map((staffGroup: { const: number, title: string }) => {
      return {
        id: staffGroup.const,
        name: staffGroup.title,
      };
    });
  }

  get data(): StepData {
    return { staffGroups: (this.props.draft || {}).content.staffGroups || [] };
  }

  get schema() {
    return { staffGroups: (this.props.schema || {}).properties.staffGroups };
  }

  isValid = (data: Object) => {
    if (!this.validate || !data) {
      return false;
    }

    return !data.staffGroups || this.validate(data.staffGroups);
  };

  handleSubmit = () => {
    if (this.props.handleSubmit) {
      this.props.setDraft({
        ...this.props.draft,
        content: {
          ...this.props.draft?.content,
          staffGroups: this.data.staffGroups,
        },
      });
      this.props.handleSubmit({}, true);
    }
  };

  handleChange = (data: Object) => {
    this.props.setDraft({
      ...this.props.draft,
      content: {
        ...this.props.draft?.content,
        staffGroups: data,
      },
    });
  };

  render() {
    if (!this.props.editable) {
      return <div />;
    }

    return (
      <React.Fragment>
        <div className="w-100 gs-container">
          {this.schema && this.data ? (
            <StaffGroupsForm
              data={this.data.staffGroups}
              options={this.staffGroupsList}
              onChange={this.handleChange}
            />
          ) : null}
        </div>
        {this.props.editing ? null : (
          <div className="w-100 d-flex justify-content-end mt-5 mb-3">
            <Button
              materialIcon="fast_forward"
              onClick={this.handleSubmit}
              disabled={!this.isValid(this.data)}
            >
              {this.props.t("NEXT")}
            </Button>
          </div>
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    schema: serviceSelectors.getAlertSchema(state),
    draft: serviceSelectors.getAlertDraft(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreator.SetAlertDraft,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(StepRestrictStaffGroups);
